<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getAllDiscountsAction } from '@/api/functions/discounts/get-all-discounts-action'
import { getUserDetailAction } from '@/api/functions/users/get-user-detail.action'
import { getUserDiscountsAction } from '@/api/functions/users/get-user-discounts.action'
import { useQueryApi } from '@/composables/useApi'
import AssignDiscountModal from '../partials/AssignDiscountModal.vue'
import UserDiscountCard from '../partials/UserDiscountCard.vue'

const route = useRoute()
const userId = route.params.id as string
const showAssignDiscountModal = ref(false)
const currentPage = ref(1)

const tabs = [
  { title: 'اطلاعات اولیه', link: `/users/${userId}` },
  { title: 'تخفیف‌ها', link: `/users/${userId}/discounts` },
  { title: 'شماره حساب‌ها', link: `/users/${userId}/accounts` },
  { title: 'تراکنش‌ها', link: `/users/${userId}/transactions` },
  { title: 'کاربران دعوت شده', link: `/users/${userId}/invited-users` },
]

const {
  data: userDetail,
  // isLoading: isUserDetailLoading,
  // isError: isUserDetailError,
  // refetch: refetchDetail
} = useQueryApi({
  queryKey: ['user-detail', userId],
  queryFn: () => getUserDetailAction(userId),
  enabled: !!userId,
})

const {
  data: userDiscounts,
  isLoading: isUserDiscountsLoading,
  // isError: isUserDiscountsError,
  refetch: refetchDiscounts,
} = useQueryApi({
  queryKey: computed(() => ['user-discounts', userId, currentPage.value]),
  queryFn: () => getUserDiscountsAction(userId, currentPage.value),
  enabled: !!userId,
})

const {
  data: allDiscounts,
  // isLoading: isAllDiscountsLoading,
  // isError: isAllDiscountsError,
  // refetch: refetchAllDiscounts
} = useQueryApi({
  queryKey: ['all-discounts'],
  queryFn: () => getAllDiscountsAction(),
  staleTime: 1000 * 60 * 5,
})

const userData = computed(() => ({
  detail: userDetail.value?.data,
  discounts: userDiscounts.value?.data,
}))

const discounts = computed(() => userDiscounts.value?.data || [])
const pagination = computed(() => userDiscounts.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)

const userStatusText = computed(() => {
  const status = userData?.value?.detail?.status

  switch (status) {
    case 'VERIFIED':
      return 'تایید شده'
    case 'EMAIL_VERIFICATION_PENDING':
      return 'ثبت نام اولیه'
    case 'MOBILE_REGISTRATION_PENDING':
      return 'در انتظار ثبت شماره موبایل'
    case 'IDENTIFICATION_REGISTRATION_PENDING':
      return 'در انتظار ارسال مدارک هویتی'
    case 'IDENTIFICATION_VERIFICATION_PENDING':
      return 'در انتظار تایید مدارک هویتی'
    case 'MOBILE_VERIFICATION_PENDING':
      return 'در انتظار تایید شماره موبایل'
    default:
      return ''
  }
})

const goToPage = (page: number) => {
  currentPage.value = page
  refetchDiscounts()
}
</script>

<template>
  <div class="mb-9">
    <AssignDiscountModal v-model="showAssignDiscountModal" :userId="userId" :allDiscounts="allDiscounts"
      @confirm="console.log('Done!')" @cancel="showAssignDiscountModal = false" />

    <router-link to="/users"
      class="w-fit mb-4 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-6">
      <div class="w-full lg:w-auto flex flex-row justify-between items-center gap-8 ">
        <div class="text-[#1b325a] text-[22px]">
          کاربر شماره <span class="font-fa">{{ userData?.detail?.code }}</span>
        </div>
        <div class="w-auto flex flex-row items-center justify-between lg:justify-start gap-2">
          <!-- status  -->
          <div
            class="w-fit flex items-center bg-[#d5ddea] text-[#2d5396] justify-center text-[13px] font-medium h-10 rounded-[10px] px-5 me-1">
            {{ userStatusText }}
          </div>
        </div>
      </div>
    </div>

    <Tabs :tabs="tabs" :isAdminPanel="true" class="mt-8">
      <template #actions>
        <div class="flex items-center gap-2 pb-1">
          <Button :isAdmin="true" @click="showAssignDiscountModal = true"
            class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
            اختصاص تخفیف
          </Button>
        </div>
      </template>
    </Tabs>

    <!-- 👉 Main Cards && Pagination  -->
    <div class="mt-20 lg:mt-8 ">
      <div v-if="isUserDiscountsLoading" class="text-center py-8 text-gray-500">
        در حال دریافت اطلاعات ...
      </div>
      <div v-else>
        <div v-if="discounts?.length > 0" class="mt-8 flex flex-col gap-6">
          <UserDiscountCard v-for="(discount, index) in discounts" :key="`card-item-${index}`" :discount="discount"
            :user-id="userId" @confirmDiscount="refetchDiscounts" />


          <Pagination v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage" :totalItems="totalItems"
            @update:currentPage="goToPage" :isAdmin="true" />
        </div>

        <div v-else
          class="w-full flex flex-col items-center gap-7 rounded-xl bg-[#f5f7fa] text-[#1b325a] text-[21px] font-medium p-10 pt-12">
          <img src="/svg/close-svgrepo-com.svg" width="58" height="58" alt="close-icon" />
          <span>محتوایی یافت نشد</span>
        </div>
      </div>
    </div>
  </div>
</template>
